import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { setPage, setLimit, setSortBy, setDefaultSort } from "../../store/slices/holidayBalancesSlice.ts";
import { RootState } from "../../store/index.ts";
import { fixedHeadStyle, fixedStyle } from "../../utils/constanst.js";
import { toCamelCase } from "../../hooks/toCameCase.tsx";

interface HolidayBalancesFilteredTableProps {
  vacationType: string;
}

const HolidayBalancesFilteredTable: React.FC<HolidayBalancesFilteredTableProps> = ({ vacationType }) => {
  const dispatch = useDispatch();
  const { total, page, limit, balances, sortBy, defaultSort } = useSelector(
    (state: RootState) => state.holidayBalances
  );

  const handlePageChange = (newPage: number) => {
    dispatch(setPage(newPage));
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(event.target.value);
    dispatch(setLimit(newLimit));
    dispatch(setPage(0));
  };

  const setSort = (sort: string) => {
    const sortOrder =
      sortBy !== sort ? "asc" : defaultSort === "asc" ? "desc" : "asc";
    dispatch(setSortBy(sort));
    dispatch(setPage(0));
    dispatch(setDefaultSort(sortOrder));
  };

  const getIconSort = () => {
    return (
      <i
        className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"}`}
      />
    );
  };

  const formatPartnerName = (partner: any) => {
    const fullName = [
      partner.person?.firstName || '',
      partner.person?.lastName || '',
      partner.person?.secondLastName || '',
    ]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    
    return toCamelCase(fullName);
  };

  const formatPeriod = (holidayPeriod: any) => {
    return `${holidayPeriod.startYear}-${holidayPeriod.endYear}`;
  };

  const getColumnTitle = () => {
    switch (vacationType) {
      case "pendingDays":
        return "Días Pendientes";
      case "usedVacations":
        return "Días Usados";
      default:
        return "Días";
    }
  };

  const getColumnValue = (balance: any) => {
    switch (vacationType) {
      case "pendingDays":
        return balance.daysRemaining;
      case "usedVacations":
        return balance.daysTaken;
      default:
        return 0;
    }
  };

  const getBadgeClass = () => {
    switch (vacationType) {
      case "pendingDays":
        return "bg-warning text-dark";
      case "usedVacations":
        return "bg-success";
      default:
        return "bg-secondary";
    }
  };

  return (
    <React.Fragment>
      <div className="table-responsive mb-3" style={{ overflowY: "auto", maxHeight: '500px' }}>
        <table className="table table-hover table-panel text-nowrap align-middle mb-0">
          <thead style={{ cursor: "pointer" }}>
            <tr style={{ ...fixedHeadStyle } as React.CSSProperties}>
              <th style={{ ...fixedStyle } as React.CSSProperties} onClick={() => setSort("id")}> 
                ID {sortBy === "id" && getIconSort()}
              </th>
              <th onClick={() => setSort("partnerName")}> 
                Colaborador {sortBy === "partnerName" && getIconSort()}
              </th>
              <th onClick={() => setSort("period")}> 
                Período {sortBy === "period" && getIconSort()}
              </th>
              <th onClick={() => setSort(vacationType === "pendingDays" ? "daysRemaining" : "daysTaken")}> 
                {getColumnTitle()} {sortBy === (vacationType === "pendingDays" ? "daysRemaining" : "daysTaken") && getIconSort()}
              </th>
              <th onClick={() => setSort("totalDays")}> 
                Días Totales {sortBy === "totalDays" && getIconSort()}
              </th>
              <th onClick={() => setSort("createdAt")}> 
                Fecha Creación {sortBy === "createdAt" && getIconSort()}
              </th>
            </tr>
          </thead>
          <tbody>
            {balances?.length > 0 ? (
              balances.map((balance, index) => (
                <tr key={index}>
                  <td style={{ ...fixedStyle } as React.CSSProperties}>{balance.id}</td>
                  <td>{formatPartnerName(balance.partner)}</td>
                  <td>
                    <span className="badge bg-primary fs-6 px-3 py-2">
                      {formatPeriod(balance.holidayPeriod)}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${getBadgeClass()} fs-6 px-3 py-2 fw-bold`}>
                      {getColumnValue(balance)} días
                    </span>
                  </td>
                  <td>
                    <span className="badge bg-info text-dark fs-6 px-3 py-2 fw-bold">
                      {balance.totalDays} días
                    </span>
                  </td>
                  <td>
                    {new Date(balance.createdAt).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    })}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6}>
                  <div className="py-4">
                    <div className="d-flex">
                      <i className="fas fa-calendar-times fa-5x text-muted mx-auto"></i>
                    </div>
                    <h5 className="text-secondary text-center fs-20px">
                      No se encontraron balances de vacaciones
                    </h5>
                    <h5 className="text-center text-secondary fw-400 fs-15px">
                      con los criterios de búsqueda seleccionados
                    </h5>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="d-md-flex align-items-center">
        <div className="me-md-auto text-md-left text-center mb-2 mb-md-0">
          Mostrando {Math.min(page * limit + balances.length, total)} de {total} balances de vacaciones
        </div>
        <div className="d-flex align-items-center">
          <span className="me-2">Mostrar:</span>
          <select className="form-select me-3" value={limit} onChange={handleLimitChange}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <ul className="pagination mb-0 justify-content-center">
          <li className={`page-item ${page === 0 && "disabled"}`}>
            <a
              href="#/"
              className="page-link"
              onClick={() => handlePageChange(page - 1)}
            >
              Anterior
            </a>
          </li>
          <li
            className={`page-item ${
              total <= page * limit + balances.length && "disabled"
            }`}
          >
            <a
              href="#/"
              className="page-link"
              onClick={() => handlePageChange(page + 1)}
            >
              Siguiente
            </a>
          </li>
        </ul>
      </div>
    </React.Fragment>
  );
};

export default HolidayBalancesFilteredTable;


